// Tiers
export type ProfTier = 'trial' | 'free' | 'essential' | 'professional'
export type BillingInterval = 'monthly' | 'yearly'
export type PaidTier = 'essential' | 'professional'

// GET /api/prof/subscription/
export interface ProfSubscriptionResponse {
  tier: ProfTier
  effective_tier: ProfTier
  is_trial_expired: boolean
  is_paid: boolean
  max_projects_count: number | null
  trial_started_at: string | null
  trial_ends_at: string | null
  subscription_status: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  scheduled_tier: ProfTier | null
  scheduled_effective_at: string | null
  billing_interval: BillingInterval | null
}

// POST /api/prof/subscription/subscribe/
export interface SubscribeRequest {
  tier: PaidTier
  billing_interval: BillingInterval
  success_url: string
  cancel_url: string
}
export interface SubscribeResponse {
  checkout_url: string
  stripe_session_id: string
}

// POST /api/prof/subscription/change/
export interface ChangeSubscriptionRequest {
  tier: PaidTier
  billing_interval: BillingInterval
}
export interface ChangeSubscriptionResponse {
  status: 'upgraded' | 'downgrade_scheduled'
  tier?: ProfTier
  effective?: 'immediately'
  current_tier?: ProfTier
  scheduled_tier?: ProfTier
  effective_at?: string
}

// POST /api/prof/subscription/cancel/
export interface CancelSubscriptionResponse {
  status: 'canceled'
  effective_at: string | null
}

// POST /api/prof/subscription/reactivate/
export interface ReactivateSubscriptionResponse {
  status: 'reactivated'
}

// POST /api/prof/subscription/preview-price/
export interface PreviewPriceRequest {
  tier: PaidTier
  billing_interval: BillingInterval
}
export interface PreviewPriceResponse {
  net_amount: number
  tax_amount: number
  gross_amount: number
  tax_rate_percent: number
  interval: 'month' | 'year'
}

// GET /api/prof/subscription/upcoming-invoice/
export interface UpcomingInvoiceResponse {
  subtotal: number
  tax: number
  total: number
  next_payment_date: string | null
  lines: Array<{
    description: string
    amount: number
    period_start: string | null
    period_end: string | null
  }>
}

// GET /api/prof/subscription/invoices/
export interface InvoicesResponse {
  invoices: Array<{
    id: string
    number: string
    status: string
    amount_due: number
    amount_paid: number
    currency: string
    created: string
    period_start: string | null
    period_end: string | null
    invoice_pdf: string | null
    hosted_invoice_url: string | null
  }>
  has_more: boolean
}

// GET /api/prof/subscription/plans/
export interface SubscriptionPlanInfo {
  key: string
  name: string
  tier: 'essential' | 'professional'
  net_amount: number
  gross_amount: number | null
  tax_amount: number | null
  tax_rate_percent: number | null
  interval: 'monthly' | 'yearly'
  currency: string
}

export interface SubscriptionPlansResponse {
  plans: SubscriptionPlanInfo[]
  has_billing_address: boolean
  billing_country: string
}

export const PLAN_PRICES = {
  essential: { monthly: 9.90, yearly: 99.00 },
  professional: { monthly: 34.99, yearly: 299.88 },
} as const

export const TIER_LIMITS = {
  trial: { max_projects: 1 },
  free: { max_projects: 1 },
  essential: { max_projects: 10 },
  professional: { max_projects: null },
} as const
